import type { ComponentType } from "react";
import type { RouteObject } from "react-router-dom";
import { importOrReload } from "@app/router/import-or-reload";

type LazyRouteModule = Pick<RouteObject, "Component" | "ErrorBoundary" | "loader">;

function lazyNamed<T extends Record<string, ComponentType>>(
  factory: () => Promise<T>,
  exportName: keyof T & string,
): () => Promise<LazyRouteModule> {
  return () =>
    importOrReload(factory).then((module) => ({
      Component: module[exportName],
    }));
}

// Public
export const lazyLandingPage = lazyNamed(
  () => import("@pages/public/landing-page"),
  "LandingPage",
);
export const lazySearchPage = lazyNamed(
  () => import("@pages/public/search-page"),
  "SearchPage",
);
export const lazyNotFoundPage = lazyNamed(
  () => import("@pages/public/not-found-page"),
  "NotFoundPage",
);

// Auth
export const lazySignInPage = lazyNamed(
  () => import("@pages/auth/sign-in-page"),
  "SignInPage",
);
export const lazySignUpPage = lazyNamed(
  () => import("@pages/auth/sign-up-page"),
  "SignUpPage",
);
export const lazyVerifyPage = lazyNamed(
  () => import("@pages/auth/verify-page"),
  "VerifyPage",
);
export const lazyForgetPasswordPage = lazyNamed(
  () => import("@pages/auth/forget-password-page"),
  "ForgetPasswordPage",
);
export const lazyResetPasswordPage = lazyNamed(
  () => import("@pages/auth/reset-password-page"),
  "ResetPasswordPage",
);
export const lazyAuthCallbackPage = lazyNamed(
  () => import("@pages/auth/auth-callback-page"),
  "AuthCallbackPage",
);

// Layouts (split from page bundles)
export const lazyPublicLayout = lazyNamed(
  () => import("@layouts/public-layout"),
  "PublicLayout",
);
export const lazyAuthLayout = lazyNamed(
  () => import("@layouts/auth-layout"),
  "AuthLayout",
);
export const lazyAppShellLayout = lazyNamed(
  () => import("@layouts/app-shell-layout"),
  "AppShellLayout",
);
export const lazyAccountSectionLayout = lazyNamed(
  () => import("@layouts/account-section-layout"),
  "AccountSectionLayout",
);

// App pages
export const lazyHomePage = lazyNamed(
  () => import("@pages/app/home-page"),
  "HomePage",
);
export const lazyCommutesPage = lazyNamed(
  () => import("@pages/app/commutes-page"),
  "CommutesPage",
);
export const lazyCommuteDetailPage = lazyNamed(
  () => import("@pages/app/commute-detail-page"),
  "CommuteDetailPage",
);
export const lazyRequestsPage = lazyNamed(
  () => import("@pages/app/requests-page"),
  "RequestsPage",
);
export const lazyRequestDetailPage = lazyNamed(
  () => import("@pages/app/request-detail-page"),
  "RequestDetailPage",
);
export const lazyNotificationsPage = lazyNamed(
  () => import("@pages/app/notifications-page"),
  "NotificationsPage",
);
export const lazyAccountPage = lazyNamed(
  () => import("@pages/app/account-page"),
  "AccountPage",
);
export const lazyProfilePage = lazyNamed(
  () => import("@pages/app/profile-page"),
  "ProfilePage",
);
export const lazyFeedbackPage = lazyNamed(
  () => import("@pages/app/feedback-page"),
  "FeedbackPage",
);
export const lazyCarsPage = lazyNamed(
  () => import("@pages/app/cars-page"),
  "CarsPage",
);
export const lazyBillingPage = lazyNamed(
  () => import("@pages/app/billing-page"),
  "BillingPage",
);
export const lazyNotificationSettingsPage = lazyNamed(
  () => import("@pages/app/notification-settings-page"),
  "NotificationSettingsPage",
);
